/**
 * Стакан и токены Polymarket прямо со страницы, без нашего сервера.
 *
 * Gamma и CLOB отвечают с `access-control-allow-origin: *`, а у `/app` нет CSP.
 * Нужны окну «Не прошло» (живой стакан обоих исходов) и наливу по кошельку
 * (токен ноги сверяется с `id` события перевода).
 */

import { PRIVATE_MIRROR, RU_FEED } from '../lib/feedContext';
import { exactCupisTokens } from './cupisPoly';
import type { Level } from '../lib/exit';
import type { RowLeg } from '../lib/row';
import { polyEventSlug, polyPinnedMarket } from '../poly/coupon';
import { normName } from './liveSight';
import { sameName } from './polyNames';

const GAMMA = 'https://gamma-api.polymarket.com/markets';
const GAMMA_EVENT = 'https://gamma-api.polymarket.com/events';
const CLOB = 'https://clob.polymarket.com/book';
const CLOB_MARKETS = 'https://clob.polymarket.com/markets';

export interface LegTokens {
  /** Токен исхода ноги. */
  mine: string;
  /** Токен второго исхода того же рынка; `null` — рынок не выбран в ссылке. */
  other: string | null;
}

export interface GammaMarket {
  slug?: string;
  sportsMarketType?: string;
  question?: string;
  outcomes?: unknown;
  clobTokenIds?: unknown;
  /** Polymarket Protocol V2: массив строк (не JSON-encoded). */
  positionIds?: unknown;
  /** "v1" — CTF (clobTokenIds), "v2" — Protocol V2 (positionIds). */
  version?: string;
  /** Condition ID рынка — для fallback через CLOB /markets/{conditionId}. */
  conditionId?: string;
  active?: boolean;
  closed?: boolean;
  acceptingOrders?: boolean;
  enableOrderBook?: boolean;
}

/**
 * Выбирает массив токенов рынка с учётом версии протокола Polymarket.
 * V2 → positionIds (array), V1 → clobTokenIds (JSON-encoded array).
 * Если version не задан — пробуем оба поля в порядке приоритета.
 */
function assetIdsOf(market: GammaMarket): string[] {
  if (market.version === 'v2') {
    // V2: positionIds — уже массив, не JSON
    const pos = Array.isArray(market.positionIds)
      ? (market.positionIds as unknown[]).filter((x): x is string => typeof x === 'string')
      : [];
    return pos;
  }
  // V1 или отсутствующий version: сначала clobTokenIds
  const fromClob = parseJsonList(market.clobTokenIds);
  if (fromClob.length === 2) return fromClob;
  // Fallback: оба поля могут быть заполнены — используем positionIds если есть
  if (Array.isArray(market.positionIds)) {
    const pos = (market.positionIds as unknown[]).filter((x): x is string => typeof x === 'string');
    if (pos.length === 2) return pos;
  }
  return fromClob;
}

/** `marketSlug` и `outcomeIndex` из ссылки или ключа ноги. */
export function slugOf(
  url: string | null,
  marketKey?: string | null,
  side?: string | null,
): { slug: string; index: 0 | 1 } | null {
  if (url === null) return null;
  return polyPinnedMarket(url, marketKey, side);
}

export function parseJsonList(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((item): item is string => typeof item === 'string');
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Имя с котировки конторы, не H=0 / A=1. Нет одного попадания — молчим.
 *
 * Сначала как есть (`normName`): так сходятся латиница и латиница. Потом через
 * транслитерацию (`sameName`): лента русская, `outcomes` английские, и без
 * второго шага сторона не называлась ни разу.
 */
export function indexByName(outcomes: string[], want: string): 0 | 1 | null {
  const need = normName(want);
  if (need.length < 2) return null;
  const pair = outcomes.slice(0, 2);
  const found = (match: (outcome: string) => boolean): 0 | 1 | null => {
    const hits: Array<0 | 1> = [];
    for (let i = 0; i < pair.length; i += 1) {
      if (match(pair[i] ?? '')) hits.push(i as 0 | 1);
    }
    return hits.length === 1 ? hits[0]! : null;
  };
  const direct = found((outcome) => {
    const one = normName(outcome);
    return one.length >= 2 && (one.includes(need) || need.includes(one));
  });
  if (direct !== null) return direct;
  return found((outcome) => sameName(want, outcome));
}

/**
 * Токены рынка. `pinned` — индекс исхода пришёл из ссылки ноги; ему можно
 * верить. Синтетический индекс (взяли ноль, потому что в ссылке его не было)
 * верить нельзя: у тенниса slug события совпадает со slug рынка-победителя, и
 * старый код в этом случае отдавал исход №0 — то есть мог купить соперника.
 *
 * Рынок выбирается только по slug из ссылки. Выбирать «любой, где сошлось имя»
 * нельзя: в одном событии рядом с матчем лежат «победитель первого сета» и
 * «второго» с теми же двумя именами, и это другая ставка.
 */
export function tokensFromMarkets(
  markets: readonly GammaMarket[],
  pin: { slug: string; index: 0 | 1 } | null,
  want: string | null,
  pinned = true,
): LegTokens | null {
  const bySlug =
    pin === null
      ? null
      : (markets.find((one) => one.slug === pin.slug) ??
        // Точный префикс: market slug начинается с pin.slug + дефис.
        // НЕ берём если это сет/период (first-set-winner, second-set-winner и т.п.)
        // — они должны совпадать по точному slug, а не по prefix.
        markets.find((one) =>
          typeof one.slug === 'string' &&
          one.slug.startsWith(`${pin.slug}-`) &&
          // Исключаем рынки с суффиксами периодов/сетов (set/period/map)
          !/-(first|second|third|fourth|fifth|set|period|map)-/.test(one.slug.slice(pin.slug.length)),
        ) ??
        // обратное: market slug является префиксом pin.slug (старая логика)
        markets.find((one) => typeof one.slug === 'string' && pin.slug.startsWith(`${one.slug}-`)));
  // Единственный рынок годится только там, где slug не задан: у события их
  // много, и одинокий «победитель первого сета» — не матч.
  const picked = bySlug ?? (pin === null && markets.length === 1 ? markets[0] : null);

  // Футбол и другие 3-исходные события: рынки вида "Will {team} win?" (Yes/No).
  // Slug рынка: event-slug + "-home" / "-away" / "-draw".
  // Ищем рынок по вопросу если slug не совпал.
  if ((picked === undefined || picked === null) && want !== null && markets.length > 1) {
    const need = normName(want);
    // Сначала ищем без draw-рынков (чтобы "Ningbo" не попал в "...vs Ningbo...end in draw?")
    const nonDraw = markets.filter((m) => typeof m.slug !== 'string' || !m.slug.endsWith('-draw'));
    const searchPool = nonDraw.length > 0 ? nonDraw : [...markets];
    const byQuestion = searchPool.find((m) => {
      if (typeof m.question !== 'string') return false;
      const q = normName(m.question);
      return q.includes(need) || need.includes(q.slice(0, Math.max(4, need.length - 2)));
    }) ?? (need === 'draw' ? markets.find((m) => m.slug?.endsWith('-draw')) : undefined);
    if (byQuestion !== undefined) {
      const ids = assetIdsOf(byQuestion);
      // Yes/No рынок: "Yes" = index 0, "No" = index 1. Покупаем "Yes".
      if (ids.length === 2) return { mine: ids[0]!, other: ids[1]! };
    }
  }

  if (picked === undefined || picked === null) return null;
  const ids = assetIdsOf(picked);
  if (ids.length !== 2) return null;
  const byName = want ? indexByName(parseJsonList(picked.outcomes), want) : null;
  if (byName !== null) return { mine: ids[byName]!, other: ids[1 - byName]! };
  if (pin !== null && pinned && bySlug === picked) {
    return { mine: ids[pin.index]!, other: ids[1 - pin.index]! };
  }
  return null;
}

const TOKEN_CACHE_MAX = 500;
const tokenCache = new Map<string, LegTokens>();

function lruSet(key: string, value: LegTokens): void {
  if (tokenCache.size >= TOKEN_CACHE_MAX) {
    // Map итерируется в порядке вставки — удаляем самый старый
    const oldest = tokenCache.keys().next().value;
    if (oldest !== undefined) tokenCache.delete(oldest);
  }
  tokenCache.set(key, value);
}

/** Персистентный хранитель промахов токенов (localStorage + in-memory). */
const MISS_LS_KEY = 'forq_token_miss_v1';
const MISS_PERSIST_MS = 5 * 60 * 1000; // 5 минут

class PersistentMissStore {
  private readonly mem = new Map<string, number>();

  constructor() {
    this.rehydrate();
  }

  private rehydrate(): void {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(MISS_LS_KEY) : null;
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, number>;
      const now = Date.now();
      for (const [id, until] of Object.entries(parsed)) {
        if (until > now) this.mem.set(id, until);
      }
    } catch { /* corrupt — ignore */ }
  }

  has(key: string): boolean {
    const until = this.mem.get(key) ?? 0;
    if (until > Date.now()) return true;
    this.mem.delete(key);
    return false;
  }

  set(key: string): void {
    this.mem.set(key, Date.now() + MISS_PERSIST_MS);
    this.persist();
  }

  delete(key: string): void {
    this.mem.delete(key);
    this.persist();
  }

  private persist(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const obj: Record<string, number> = {};
      for (const [k, v] of this.mem) obj[k] = v;
      localStorage.setItem(MISS_LS_KEY, JSON.stringify(obj));
    } catch { /* localStorage full — ignore */ }
  }
}

const missStore = new PersistentMissStore();
const GAMMA_SEARCH = 'https://gamma-api.polymarket.com/public-search';

async function fetchMarkets(slug: string): Promise<GammaMarket[]> {
  const res = await fetch(`${GAMMA}?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) return [];
  const body: unknown = await res.json();
  return Array.isArray(body) ? (body as GammaMarket[]) : [];
}

async function fetchEventMarkets(slug: string): Promise<GammaMarket[]> {
  const res = await fetch(`${GAMMA_EVENT}?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) return [];
  const body: unknown = await res.json();
  const events = Array.isArray(body) ? body : [];
  const first = events[0] as { markets?: GammaMarket[] } | undefined;
  return Array.isArray(first?.markets) ? first.markets : [];
}

/**
 * Fallback для V2 рынков: если Gamma не вернул positionIds через /markets или /events,
 * зовём CLOB /markets/{conditionId} — он всегда отдаёт tokens[].token_id.
 */
async function fetchTokensByCondition(conditionId: string): Promise<string[]> {
  try {
    const res = await fetch(`${CLOB_MARKETS}/${encodeURIComponent(conditionId)}`);
    if (!res.ok) return [];
    const body = (await res.json()) as { tokens?: Array<{ token_id: string; outcome: string }> };
    return (body.tokens ?? []).map((t) => t.token_id).filter((id) => id.length > 0);
  } catch {
    return [];
  }
}

/**
 * Событие из ответа поиска. Со slug берём своё, без slug — первое.
 *
 * Первое попавшееся опасно: по запросу «atp khachan fery» Gamma первым отдаёт
 * «победитель турнира», где та же фамилия встречается на законных основаниях.
 */
export function eventFromSearch(
  body: unknown,
  slug: string | null = null,
): { slug: string; markets: GammaMarket[] } | null {
  if (typeof body !== 'object' || body === null) return null;
  const events = (body as { events?: unknown }).events;
  if (!Array.isArray(events) || events.length === 0) return null;
  const all = events as Array<{ slug?: string; markets?: GammaMarket[] }>;
  const one = slug === null ? all[0] : all.find((event) => event.slug === slug);
  if (one === undefined) return null;
  return { slug: typeof one.slug === 'string' ? one.slug : '', markets: Array.isArray(one.markets) ? one.markets : [] };
}

/** Рынки первого события поиска Gamma. Пустой запрос не зовём. */
export function marketsFromSearch(body: unknown): GammaMarket[] {
  return eventFromSearch(body)?.markets ?? [];
}

async function fetchSearch(
  query: string,
  slug: string | null,
): Promise<{ slug: string; markets: GammaMarket[] } | null> {
  const q = query.trim();
  if (q.length < 4) return null;
  const res = await fetch(`${GAMMA_SEARCH}?q=${encodeURIComponent(q)}`);
  if (!res.ok) return null;
  return eventFromSearch(await res.json(), slug);
}

/**
 * Нормализует сокращённый sport-prefix BetBurger в канонический Polymarket prefix.
 * BetBurger присылает "ba-orl-nyk-…" вместо "nba-orl-nyk-…" и т.п.
 * Вызывается только когда Gamma не находит event по исходному slug.
 *
 * Таблица замен (проверены по реальным событиям):
 *   ba-  → nba-   (basketball/НБА)
 *   fl-  → nfl-   (american_football/НФЛ)
 *   hl-  → nhl-   (hockey/НХЛ)
 *   lb-  → mlb-   (baseball/МЛБ)
 *   tp-  → atp-   (tennis ATP)
 *   wl-  → wta-   (tennis WTA)
 *   ex-  → mex-   (Mexico football)
 *   s2-  → cs2-   (Counter-Strike 2)
 *   wsl- → nwsl-  (National Women's Soccer League)
 *   ar1- → par1-  (Paraguay Primera Division)
 *   ra-  → bra-   (Brazil football)
 *   en1- → ven1-  (Venezuela football)
 *   ru1- → uru1-  (Uruguay football)
 */
export function normalizeEventSlug(slug: string | null): string | null {
  if (slug === null) return null;
  const MAP: Record<string, string> = {
    'ba-': 'nba-',
    'fl-': 'nfl-',
    'hl-': 'nhl-',
    'lb-': 'mlb-',
    'tp-': 'atp-',
    'wl-': 'wta-',
    'ex-': 'mex-',
    's2-': 'cs2-',
    'wsl-': 'nwsl-',
    'ar1-': 'par1-',
    'ra-': 'bra-',
    'en1-': 'ven1-',
    'ru1-': 'uru1-',
  };
  for (const [bad, good] of Object.entries(MAP)) {
    if (slug.startsWith(bad)) return good + slug.slice(bad.length);
  }
  return null;
}

/**
 * Что спросить у поиска, когда рынок по slug не нашёлся. Из slug ссылки —
 * имена латиницей, которых в русской ленте нет вовсе. Дату и голые числа
 * выбрасываем: с ними Gamma уводит на турнирные рынки.
 */
export function searchWords(slug: string | null): string | null {
  if (slug === null) return null;
  const parts = slug
    .split('-')
    .filter((one) => one.length >= 3 && !/^\d+$/.test(one) && !/^\d{4}$/.test(one));
  const words = parts.filter((one) => !/^(atp|wta|epl|nba|nhl|mlb|lol|csgo|cs2|dota)$/.test(one));
  const picked = words.length >= 2 ? words : parts;
  const query = picked.join(' ').trim();
  return query.length >= 4 ? query : null;
}

export async function tokensOf(
  leg: Pick<RowLeg, 'url' | 'asset'> & {
    marketKey?: string | null;
    side?: string | null;
    want?: string | null;
    query?: string | null;
  },
): Promise<LegTokens | null> {
  if (PRIVATE_MIRROR || RU_FEED) {
    // Assets are supplied only by the backend's exact market matcher.
    if (leg.asset) return { mine: leg.asset, other: null };
    const event = leg.url ? polyEventSlug(leg.url) : null;
    if (!event) return null;
    const key = `cupis:${event}:${leg.marketKey ?? ''}:${leg.side ?? ''}:${leg.want ?? ''}:${leg.url}`;
    const hit = tokenCache.get(key);
    if (hit) return hit;
    if (missStore.has(key)) return null;
    try {
      const found = exactCupisTokens(await fetchEventMarkets(event), leg);
      if (found) { lruSet(key, found); return found; }
    } catch { /* no unverified fallback */ }
    missStore.set(key);
    return null;
  }
  const at = slugOf(leg.url, leg.marketKey, leg.side);
  const event = leg.url ? polyEventSlug(leg.url) : null;
  const want = typeof leg.want === 'string' && leg.want.trim() !== '' ? leg.want.trim() : null;
  const query = typeof leg.query === 'string' && leg.query.trim() !== '' ? leg.query.trim() : null;
  const key = `${at?.slug ?? event ?? ''}#${at?.index ?? want ?? ''}#${leg.asset ?? ''}#${query ?? ''}`;
  const hit = tokenCache.get(key);
  if (hit !== undefined) return hit;
  if (missStore.has(key)) return null;
  const keep = (made: LegTokens | null): LegTokens | null => {
    if (made !== null) {
      lruSet(key, made);
      missStore.delete(key);
    } else {
      missStore.set(key);
    }
    return made;
  };
  try {
    if (at !== null) {
      const markets = await fetchMarkets(at.slug);
      const fromMarket = tokensFromMarkets(markets, at, want);
      if (fromMarket !== null) return keep(fromMarket);
      // CLOB conditionId fallback для V2 рынков у которых Gamma вернула пустой массив токенов
      for (const m of markets) {
        if (m.conditionId && m.conditionId.length > 0) {
          const ids = await fetchTokensByCondition(m.conditionId);
          if (ids.length >= 2) {
            const byName = want ? indexByName(parseJsonList(m.outcomes), want) : null;
            const idx = byName ?? at.index;
            return keep({ mine: ids[idx]!, other: ids[1 - idx] ?? null });
          }
        }
      }
      if (event !== null) {
        const eventMarkets = await fetchEventMarkets(event);
        const fromEvent = tokensFromMarkets(eventMarkets, at, want);
        if (fromEvent !== null) return keep(fromEvent);
        // CLOB conditionId fallback для V2 рынков из события
        for (const m of eventMarkets) {
          if (m.conditionId && m.conditionId.length > 0) {
            const ids = await fetchTokensByCondition(m.conditionId);
            if (ids.length >= 2) {
              const byName = want ? indexByName(parseJsonList(m.outcomes), want) : null;
              const idx = byName ?? at.index;
              return keep({ mine: ids[idx]!, other: ids[1 - idx] ?? null });
            }
          }
        }
      }
    } else if (event !== null) {
      // Индекс исхода в ссылке не назван: сторону называет только имя.
      // Для 3-исходных (футбол) tokensFromMarkets ищет рынок по question.
      // Если BetBurger прислал сокращённый slug (ba- вместо nba-, fl- вместо nfl- и т.п.),
      // нормализуем и пробуем ещё раз.
      const normEvent = normalizeEventSlug(event);
      // Сначала пробуем оригинальный slug, потом нормализованный.
      const slugsToTry = normEvent !== null ? [event, normEvent] : [event];
      for (const trySlug of slugsToTry) {
        const eventMarkets = await fetchEventMarkets(trySlug);
        if (eventMarkets.length === 0) continue;
        const fromEvent = tokensFromMarkets(
          eventMarkets,
          { slug: trySlug, index: 0 },
          want,
          false,
        );
        if (fromEvent !== null) return keep(fromEvent);
        // CLOB conditionId fallback
        for (const m of eventMarkets) {
          if (m.conditionId && m.conditionId.length > 0) {
            const ids = await fetchTokensByCondition(m.conditionId);
            if (ids.length >= 2) {
              const byName = want ? indexByName(parseJsonList(m.outcomes), want) : null;
              const idx = byName ?? 0;
              return keep({ mine: ids[idx]!, other: ids[1 - idx] ?? null });
            }
          }
        }
      }
    }
    // Имена в поиск идут латиницей из slug: по русским Gamma не находит ничего.
    const needle = searchWords(at?.slug ?? event) ?? query ?? want;
    if (needle !== null && needle.length >= 4 && want !== null) {
      // Событие ищем по slug события: `at.slug` — это рынок внутри него.
      const found = await fetchSearch(needle, event);
      const fromSearch =
        found === null
          ? null
          : tokensFromMarkets(found.markets, at ?? { slug: found.slug, index: 0 }, want, at !== null);
      if (fromSearch !== null) return keep(fromSearch);
    }
  } catch {
    /* ниже — запас по уже известному токену */
  }
  if (leg.asset !== null && leg.asset !== '') return keep({ mine: leg.asset, other: null });
  return keep(null);
}

export interface PolyBook {
  bids: Level[];
  asks: Level[];
}

export function bookOf(token: string): Promise<PolyBook | null> {
  return fetch(`${CLOB}?token_id=${encodeURIComponent(token)}`)
    .then(async (res) => {
      if (!res.ok) return null;
      const body = (await res.json()) as {
        bids?: { price: string; size: string }[];
        asks?: { price: string; size: string }[];
      };
      const side = (rows?: { price: string; size: string }[]): Level[] =>
        (rows ?? []).map((one) => [String(one.price), String(one.size)] as const);
      return { bids: side(body.bids), asks: side(body.asks) };
    })
    .catch(() => null);
}
